<template>
  <div>
    <v-row>
      <v-col>
        <v-card class="py-10 px-5 rounded-lg fill-height">
          <v-row
            no-gutters
          >
            <v-col
              cols="3"
            >
              <v-row>
                <v-col cols="auto">
                  <v-avatar
                    size="100px"
                    class="elevation-1"
                  >
                    <v-img
                      :src="player.photo_url"
                      width="100px"
                      height="100px"
                      class="rounded"
                    >
                      <template v-slot:placeholder>
                        <v-icon x-large>
                          fal fa-user
                        </v-icon>
                      </template>
                    </v-img>
                  </v-avatar>
                </v-col>
                <v-col>
                  <v-row
                    align="center"
                    class="title flex-column"
                  >
                    <v-col
                      class="grey--text text--darken-4 d-flex flex-column"
                    >
                      <span class="font-weight-bold">
                        {{ player.last_name }}
                      </span>
                      <span class="subtitle-2">
                        {{ player.first_name }}
                      </span>
                    </v-col>
                    <v-col class="subtitle-2 d-flex flex-column">
                      <div>
                        {{ $t('individual_analysis.data.team') }}: {{ team.name }}
                      </div>
                      <div class="mt-2">
                        {{ $t('individual_analysis.data.position') }}:
                        <v-chip
                          label
                          small
                        >
                          {{ player.player_positions && player.player_positions.length > 0 ? player.player_positions.map(p => $t(`player_position.${toCamelCase(p.name)}.short`)).join(', ') : '-' }}
                        </v-chip>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row class="pt-3">
                <v-col>
                  <v-btn
                    class="text-none"
                    color="primary"
                    outlined
                    small
                    @click="eventPlayerDialog = true"
                  >
                    {{ $t('button.event_player_details') }}
                  </v-btn>
                </v-col>
              </v-row>
              <v-row v-if="eventParentPlayerStats.UXPCSTDR ">
                <v-col>
                  <div>
                    {{ $t('button.sensor_data_duration') }}: <span class="font-weight-medium">{{ eventParentPlayerStats.UXPCSTDR }}</span> min
                  </div>
                </v-col>
              </v-row>
            </v-col>
            <v-col class="mx-5">
              <v-row class="fill-height">
                <v-col
                  v-for="variable in itlVariables"
                  :key="variable.value"
                  cols="6"
                  align-self="center"
                >
                  <v-card
                    rounded="lg"
                    outlined
                    :color="variable.colorable && eventParentPlayerStats[variable.value] ? getColorForValue(eventParentPlayerStats[variable.value], variable.max, variable.reverse) : 'grey lighten-2'"
                  >
                    <v-card-text class="d-flex flex-column align-center text-center py-5 px-1">
                      <span class="text-h6 font-weight-bold grey--text text--grey-100">{{ eventParentPlayerStats[variable.value] ? eventParentPlayerStats[variable.value] : '-' }}</span>
                      <span class="mt-4 font-weight-medium grey--text text--grey-100">{{ variable.text }}</span>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-col>
            <v-col
              cols="3"
              class="d-flex justify-center"
            >
              <human-body-female-front-muscles
                v-if="player.gender === 'female'"
                style="height: 220px; width: 130px"
                :stats="player.player_stats"
              />
              <human-body-front-muscles
                v-else
                style="height: 220px; width: 130px"
                :stats="player.player_stats"
              />
              <human-body-female-back-muscles
                v-if="player.gender === 'female'"
                style="height: 220px; width: 130px"
                :stats="player.player_stats"
              />
              <human-body-back-muscles
                v-else
                style="height: 220px; width: 130px"
                :stats="player.player_stats"
              />
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="!singlePlayerEvent && hasPlayerExerciseTiming && !hasExerciseTiming">
      <v-spacer />
      <v-col cols="auto">
        <v-select
          v-model="selectedExercise"
          :items="playerTrainingExercises"
          item-value="id"
          return-object
          hide-details
          clearable
          solo
          :label="$t('dropdown.select_exercise.title')"
          style="width: 300px"
        >
          <template v-slot:selection="{ item }">
            <span v-if="item.type === 'superset'">{{ $t("training_builder.superset") }}</span>
            <span v-else-if="item.type === 'exercise'">{{ item.name }}</span>
            <span v-else>{{ $t("training_builder.pause") }}</span>
          </template>
          <template v-slot:item="{ item }">
            <v-list-item-content
              v-if="item.type === 'superset'"
            >
              <v-list-item-title>
                {{ $t("training_builder.superset") }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ item.exercises.map(e => e.name ? e.name : $t('training_builder.pause')).join(' • ') }}
              </v-list-item-subtitle>
              <v-list-item-subtitle
                v-if="item.exercises[0].exercise_start_at"
                class="mt-1"
              >
                {{ $dayjs(item.exercises[0].exercise_start_at).format('HH:mm:ss') }} - {{ $dayjs(item.exercises[0].exercise_stop_at).format('HH:mm:ss') }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-content
              v-else-if="item.type === 'exercise'"
            >
              <v-list-item-title v-text="item.name" />
              <v-list-item-subtitle v-if="item.exercise_start_at">
                {{ $dayjs(item.exercise_start_at).format('HH:mm:ss') }} - {{ $dayjs(item.exercise_stop_at).format('HH:mm:ss') }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-content
              v-else
            >
              <v-list-item-title>
                {{ $t("training_builder.pause") }}
              </v-list-item-title>
              <v-list-item-subtitle
                v-if="item.exercise_start_at"
                class="mt-1"
              >
                {{ $dayjs(item.exercise_start_at).format('HH:mm:ss') }} - {{ $dayjs(item.exercise_stop_at).format('HH:mm:ss') }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-select>
      </v-col>
    </v-row>

    <div v-if="selectedExercise && exerciseStats.loading">
      <v-container
        class="d-flex justify-center align-center my-16"
      >
        <v-progress-circular
          indeterminate
          size="100"
        />
      </v-container>
    </div>
    <div v-else-if="variables.loading">
      <v-container
        class="d-flex justify-center align-center my-16"
      >
        <v-progress-circular
          indeterminate
          size="100"
        />
      </v-container>
    </div>
    <div v-else>
      <template v-if="event.data.has_gps_stats || event.data.has_gps_stats === null">
        <v-row class="mt-5">
          <v-col>
            <v-row>
              <v-col cols="6">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('UXVOL')"
                  :value="playerStats.UXVOL"
                  :max="performanceMaxValues.UXVOL || event.maxValues.UXVOL"
                  :color="getColorFromValue(playerStats.UXVOL, performanceMaxValues.UXVOL || event.maxValues.UXVOL, true)"
                  class="fill-height"
                >
                  {{ playerStats.UXVOL }}
                </stat-card>
              </v-col>
              <v-col cols="6">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('UXINTS')"
                  :value="playerStats.UXINTS"
                  :max="performanceMaxValues.UXINTS || event.maxValues.UXINTS"
                  :color="getColorFromValue(playerStats.UXINTS, performanceMaxValues.UXINTS || event.maxValues.UXINTS, true)"
                  class="fill-height"
                >
                  {{ playerStats.UXINTS }}
                </stat-card>
              </v-col>
              <v-col cols="6">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('UXPCSTD')"
                  :value="playerStats.UXPCSTD"
                  :max="performanceMaxValues.UXPCSTD || event.maxValues.UXPCSTD"
                  :color="getColorFromValue(playerStats.UXPCSTD, performanceMaxValues.UXPCSTD || event.maxValues.UXPCSTD)"
                  class="fill-height"
                >
                  {{ playerStats.UXPCSTD }}
                </stat-card>
              </v-col>
              <v-col cols="6">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('UXPCSEEF')"
                  :value="playerStats.UXPCSEEF"
                  :max="performanceMaxValues.UXPCSEEF || event.maxValues.UXPCSEEF"
                  :color="getColorFromValue(playerStats.UXPCSEEF, performanceMaxValues.UXPCSEEF || event.maxValues.UXPCSEEF)"
                  class="fill-height"
                >
                  {{ playerStats.UXPCSEEF }}
                </stat-card>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="4">
            <radial-bar-chart
              class="fill-height"
              :series="[ playerStats.CALETLDLHSR_G || 0 ]"
              :plot-options="{
                radialBar: {
                  startAngle: -90,
                  endAngle: 90,
                  dataLabels: {
                    name: {
                      show: false
                    },
                    value: {
                      offsetY: playerStats.CALETLDLHSR ? -2 : 25,
                      fontSize: '24px'
                    },
                    total: {
                      show: true,
                      label: playerStats.CALETLDLHSR,
                      formatter: () => playerStats.CALETLDLHSR ? playerStats.CALETLDLHSR + ' ' + getVariableSuffix('CALETLDLHSR') : 'N/A'
                    }
                  }
                }
              }"
              :tooltip="{
                enabled: true,
                y: {
                  formatter: () => `${playerStats.CALETLDLHSR_G}%`,
                  title: {
                    formatter: () => getVariableName('CALETLDLHSR_G') + ':'
                  }
                }
              }"
              :animations="{enabled: false}"
            >
              <template v-slot:header>
                <v-card-title>
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t("card_title.high_speed_running") }}
                  </div>
                </v-card-title>
              </template>
            </radial-bar-chart>
          </v-col>
          <v-col cols="4">
            <v-row>
              <v-col cols="12">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('CACGCSMXV')"
                  :value="playerStats.CACGCSMXV"
                  :max="performanceMaxValues.CACGCSMXV || event.maxValues.CACGCSMXV"
                  :color="getColorFromValue(playerStats.CACGCSMXV, performanceMaxValues.CACGCSMXV || event.maxValues.CACGCSMXV)"
                  class="fill-height"
                >
                  {{ playerStats.CACGCSMXV }}
                </stat-card>
              </v-col>
              <v-col cols="12">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('UXAVGVEL')"
                  :value="playerStats.UXAVGVEL"
                  :max="performanceMaxValues.UXAVGVEL || event.maxValues.UXAVGVEL"
                  :color="getColorFromValue(playerStats.UXAVGVEL, performanceMaxValues.UXAVGVEL || event.maxValues.UXAVGVEL)"
                  class="fill-height"
                >
                  {{ playerStats.UXAVGVEL }}
                </stat-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="4">
            <line-chart
              type="bar"
              :height="250"
              :raw-data="speedZones"
              :chart-value-keys="[ { key: 'value', type: 'bar'} ]"
              :plot-options="{ bar: { horizontal: true, borderRadius: 4 } }"
              x-value="key"
              :grid="{ show: false }"
              :xaxis="{
                categories: speedZones.map(stat => stat.label),
                tickPlacement: 'on',
                title: { text: $t('chart_legends.distance') },
                axisBorder: { show: false }
              }"
            >
              <template v-slot:header>
                <v-card-title>
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t("card_title.distance_zone_breakdown") }}
                  </div>
                </v-card-title>
              </template>
            </line-chart>
          </v-col>
          <v-col cols="4">
            <line-chart
              type="bar"
              :height="250"
              :raw-data="variablesDistancePerMin"
              :chart-value-keys="[ { key: 'value', type: 'bar'} ]"
              :plot-options="{ bar: { horizontal: true, borderRadius: 4 } }"
              x-value="key"
              :grid="{ show: false }"
              :xaxis="{
                categories: variablesDistancePerMin.map(stat => stat.label),
                tickPlacement: 'on',
                title: { text: $t('chart_legends.distance') },
                axisBorder: { show: false }
              }"
            >
              <template v-slot:header>
                <v-card-title>
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t("card_title.per_minute_distance") }}
                  </div>
                </v-card-title>
              </template>
            </line-chart>
          </v-col>
          <v-col cols="4">
            <line-chart
              type="bar"
              :height="250"
              :raw-data="velocityZones"
              :chart-value-keys="[ { key: 'value', type: 'bar'} ]"
              :plot-options="{ bar: { horizontal: true, borderRadius: 4 } }"
              x-value="key"
              :grid="{ show: false }"
              :xaxis="{
                categories: velocityZones.map(stat => stat.label),
                tickPlacement: 'on',
                axisBorder: { show: false }
              }"
            >
              <template v-slot:header>
                <v-card-title>
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t("card_title.velocity_zone_breakdown") }}
                  </div>
                </v-card-title>
              </template>
            </line-chart>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-row class="fill-height">
              <v-col cols="6">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('CAPGCSMXAC')"
                  :value="playerStats.CAPGCSMXAC"
                  :max="performanceMaxValues.CAPGCSMXAC || event.maxValues.CAPGCSMXAC"
                  :color="getColorFromValue(playerStats.CAPGCSMXAC, performanceMaxValues.CAPGCSMXAC || event.maxValues.CAPGCSMXAC)"
                  class="fill-height"
                >
                  {{ playerStats.CAPGCSMXAC }}
                </stat-card>
              </v-col>
              <v-col cols="6">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('CAPGCSMXDC')"
                  :value="playerStats.CAPGCSMXDC"
                  :max="performanceMaxValues.CAPGCSMXDC || event.maxValues.CAPGCSMXDC"
                  :color="getColorFromValue(playerStats.CAPGCSMXDC, performanceMaxValues.CAPGCSMXDC || event.maxValues.CAPGCSMXDC)"
                  class="fill-height"
                >
                  {{ playerStats.CAPGCSMXDC }}
                </stat-card>
              </v-col>
              <v-col cols="6">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('UXACCALL')"
                  :value="playerStats.UXACCALL"
                  :max="performanceMaxValues.UXACCALL || event.maxValues.UXACCALL"
                  :color="getColorFromValue(playerStats.UXACCALL, performanceMaxValues.UXACCALL || event.maxValues.UXACCALL)"
                  class="fill-height"
                >
                  {{ playerStats.UXACCALL }}
                </stat-card>
              </v-col>
              <v-col cols="6">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('UXDECALL')"
                  :value="playerStats.UXDECALL"
                  :max="performanceMaxValues.UXDECALL || event.maxValues.UXDECALL"
                  :color="getColorFromValue(playerStats.UXDECALL, performanceMaxValues.UXDECALL || event.maxValues.UXDECALL)"
                  class="fill-height"
                >
                  {{ playerStats.UXDECALL }}
                </stat-card>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="6">
            <line-chart
              class="fill-height pb-0"
              type="bar"
              :height="220"
              :raw-data="accelerationZones"
              :chart-value-keys="[ { key: 'Decelerations', type: 'bar'}, { key: 'Accelerations', type: 'bar'}]"
              :plot-options="{ bar: { borderRadius: 5, borderRadiusApplication: 'end', borderRadiusWhenStacked: 'all', horizontal: true, barHeight: '60%'} }"
              x-value="key"
              :grid="{ show: false }"
              :xaxis="{
                categories: accelerationZones.map(stat => stat.label),
                tickPlacement: 'on',
                min: -(Math.max(...accelerationZones.map(stat => stat.Accelerations).concat(accelerationZones.map(stat => -stat.Decelerations))) + 1),
                max: Math.max(...accelerationZones.map(stat => stat.Accelerations).concat(accelerationZones.map(stat => -stat.Decelerations))) + 1,
                axisBorder: { show: false },
                labels: {
                  formatter: function(value) {
                    return Math.abs(value);
                  }
                }
              }"
              :tooltip="{
                x: {
                  formatter: function(value) {
                    return value;
                  }
                },
                y: {
                  formatter: function(value) {
                    return Math.abs(value);
                  }
                }
              }"
              :stacked="true"
            >
              <template v-slot:header>
                <v-card-title>
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t("card_title.acceleration_zone_breakdown") }}
                  </div>
                </v-card-title>
              </template>
            </line-chart>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="variable in filteredMetabolic"
            :key="variable"
            cols="3"
            class="pb-1"
          >
            <v-card rounded="lg">
              <v-card-text>
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">{{ getVariableName(variable) }}</span>
                  <span
                    class="font-weight-medium"
                    style="white-space: nowrap"
                  >{{ playerStats[variable] }} {{ getVariableSuffix(variable) }}</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
      <template v-if="event.data.has_hr_stats || event.data.has_hr_stats === null">
        <v-row class="pt-6">
          <v-col>
            <v-row class="fill-height">
              <v-col cols="6">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('HRMAX')"
                  :value="playerStats.HRMAX"
                  :max="performanceMaxValues.HRMAX || event.maxValues.HRMAX"
                  :color="getColorFromValue(playerStats.HRMAX, performanceMaxValues.HRMAX || event.maxValues.HRMAX, true)"
                  class="fill-height"
                />
              </v-col>
              <v-col cols="6">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('HRAVG')"
                  :value="playerStats.HRAVG"
                  :max="performanceMaxValues.HRAVG || event.maxValues.HRAVG"
                  :color="getColorFromValue(playerStats.HRAVG, performanceMaxValues.HRAVG || event.maxValues.HRAVG, true)"
                  class="fill-height"
                />
              </v-col>
              <v-col cols="6">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('TRIMP')"
                  :value="playerStats.TRIMP ? playerStats.TRIMP : undefined"
                  :max="performanceMaxValues.TRIMP || event.maxValues.TRIMP"
                  :color="getColorFromValue(playerStats.TRIMP, performanceMaxValues.TRIMP || event.maxValues.TRIMP)"
                  class="fill-height"
                />
              </v-col>
              <v-col cols="6">
                <stat-card
                  :loading="variables.loading"
                  :title="getVariableName('TRIMPMIN')"
                  :value="playerStats.TRIMPMIN ? playerStats.TRIMPMIN : undefined"
                  :max="performanceMaxValues.TRIMPMIN || event.maxValues.TRIMPMIN"
                  :color="getColorFromValue(playerStats.TRIMPMIN, performanceMaxValues.TRIMPMIN || event.maxValues.TRIMPMIN)"
                  class="fill-height"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col>
            <line-chart
              class="fill-height pb-0"
              type="bar"
              :height="220"
              :raw-data="hrZones"
              :chart-value-keys="[ { key: 'value', type: 'bar'} ]"
              :plot-options="{ bar: { horizontal: true, borderRadius: 4 } }"
              x-value="key"
              :grid="{ show: false }"
              :xaxis="{
                categories: hrZones.map(stat => stat.label),
                tickPlacement: 'on',
                title: { text: $t('chart_legends.time') },
                axisBorder: { show: false }
              }"
            >
              <template v-slot:header>
                <v-card-title>
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t('card_title.heart_rate_zones') }}
                  </div>
                </v-card-title>
              </template>
            </line-chart>
          </v-col>
        </v-row>
      </template>

      <template v-if="event.data.has_rr_stats || event.data.has_rr_stats === null">
        <v-row class="pt-6">
          <v-col
            v-for="variable in variablesRR"
            :key="variable.id"
            cols="3"
            class="pb-1"
          >
            <v-card rounded="lg">
              <v-card-text>
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">{{ variable.text }}</span>
                  <span class="font-weight-medium">{{ playerStats[variable.value] }}</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <v-row
        v-if="event.data.periods.length > 0 || hasExerciseTiming"
      >
        <v-spacer />
        <v-col
          v-if="event.data.periods.length > 0"
          cols="auto"
        >
          <v-btn
            class="text-none"
            :color="showPeriodAnnotations ? 'primary' : 'grey darken-2'"
            :outlined="!showPeriodAnnotations"
            small
            @click="showPeriodAnnotations = !showPeriodAnnotations; showExerciseAnnotations = false"
          >
            {{ $t('button.show_periods') }}
          </v-btn>
        </v-col>
        <v-col
          v-if="hasExerciseTiming"
          cols="auto"
        >
          <v-btn
            class="text-none"
            :color="showExerciseAnnotations ? 'primary' : 'grey darken-2'"
            :outlined="!showExerciseAnnotations"
            small
            @click="showExerciseAnnotations = !showExerciseAnnotations; showPeriodAnnotations = false"
          >
            {{ $t('button.show_exercises') }}
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-card
            class="pa-5 rounded-lg fill-height"
          >
            <v-row>
              <v-col>
                <div class="text-h6">
                  {{ $t("card_title.raw_data") }}
                </div>
              </v-col>
            </v-row>
            <div
              v-if="rawData.loadingRaw"
              class="text-center"
              style="padding: 130px 0"
            >
              <v-progress-circular
                color="grey lighten-1"
                :size="150"
                indeterminate
                :width="1"
              />
            </div>
            <div
              v-else-if="speedSeries.length === 0 && heartRateLogSeries.length === 0"
              class="text-center"
              style="padding: 130px 0"
            >
              {{ $t("data_empty") }}
            </div>
            <div v-else>
              <line-chart
                class="elevation-0"
                :series-override="[{ name: $t('performance.raw.variables.heart_rate'), data: heartRateLogSeries },{ name: $t('performance.raw.variables.speed'), data: speedSeries }]"
                :xaxis="{ type: 'datetime', labels: { datetimeUTC: false } }"
                :yaxis="[ { title: { text: $t('hr_variables.hr_bpm') }, max: 210, min: 0, seriesName: 'Heart Rate' }, { title: { text: $t('gps_variables.speed_unit') }, min: 0, seriesName: 'Speed', opposite: true } ]"
                :tooltip="{
                  x: {
                    format: 'HH:mm:ss'
                  }
                }"
                :animations="{enabled: false}"
                :colors="['#E91E63', '#008FFB']"
                :annotations="heartRateLogAnnotations"
                :height="400"
                :fill="{ type: 'solid', opacity: [1, 1] }"
              />
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="pt-4">
        <v-col>
          <v-card
            class="pa-5 rounded-lg"
          >
            <v-row>
              <v-col>
                <div class="text-h6">
                  {{ $t("card_title.efforts") }}
                </div>
              </v-col>
              <v-col cols="auto">
                <v-btn-toggle
                  v-model="tab"
                  color="primary"
                  mandatory
                >
                  <v-btn
                    value="sprints"
                    small
                    class="text-none"
                  >
                    {{ $t("button.velocity") }}
                  </v-btn>
                  <v-btn
                    value="acc"
                    small
                    class="text-none"
                  >
                    {{ $t("button.acceleration") }}
                  </v-btn>
                  <v-btn
                    value="hr"
                    small
                    class="text-none"
                  >
                    {{ $t("button.heart_rate") }}
                  </v-btn>
                </v-btn-toggle>
              </v-col>
              <v-col cols="auto">
                <v-btn-toggle
                  v-model="view"
                  color="primary"
                  mandatory
                >
                  <v-btn
                    value="visual"
                    small
                    class="text-none"
                  >
                    {{ $t("card.display_option.visual") }}
                  </v-btn>
                  <v-btn
                    value="list"
                    small
                    class="text-none"
                  >
                    {{ $t("card.display_option.list") }}
                  </v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>
            <div
              v-if="rawData.loadingEfforts"
              class="text-center"
              style="padding: 130px 0"
            >
              <v-progress-circular
                color="grey lighten-1"
                :size="150"
                indeterminate
                :width="1"
              />
            </div>
            <div
              v-else-if="!hasEfforts"
              class="text-center"
              style="padding: 130px 0"
            >
              {{ $t("data_empty") }}
            </div>
            <div v-else>
              <v-row
                align="center"
              >
                <v-col
                  cols="auto"
                  class="grey--text text--darken-2 text-caption"
                >
                  <strong>{{ tab === 'sprints' ? filteredSprints.length : tab === 'acc' ? filteredAcc.length : filteredHREfforts.length }}</strong> {{ $t("label.efforts") }}
                </v-col>
                <v-spacer />
                <v-col
                  cols="auto"
                  class="text-body-1 grey--tex  t pr-2"
                >
                  {{ $t('zones.title') }}
                </v-col>
                <v-col
                  cols="auto"
                >
                  <v-btn-toggle
                    v-model="selectedBands"
                    dense
                    multiple
                  >
                    <v-tooltip
                      v-for="val in possibleBands"
                      :key="val"
                      top
                    >
                      <template v-slot:activator="{ on }">
                        <div
                          style="position: relative;"
                          v-on="on"
                        >
                          <v-btn
                            class="ml-1"
                            :value="val"
                            small
                            min-width="40"
                            :color="getColorForTooltip(val)"
                          >
                            {{ val }}
                            <v-sheet
                              v-if="selectedBands.includes(val)"
                              class="indicator"
                              elevation="12"
                              height="5"
                              width="5"
                              style="background-color: #000000; position: absolute; top: 25px; border-radius: 50%;"
                            />
                          </v-btn>
                        </div>
                      </template>
                      <span>
                        {{ getTooltipText(val) }}
                      </span>
                    </v-tooltip>
                  </v-btn-toggle>
                </v-col>
              </v-row>
              <v-row
                align="center"
              >
                <v-col v-if="view === 'visual'">
                  <line-chart
                    v-if="tab === 'sprints'"
                    class="elevation-0"
                    type="bar"
                    :raw-data="filteredSprints"
                    :chart-value-keys="[ { key: 'max', type: 'bar', label: $t('chart_legends.velocity') }]"
                    x-value="start_time"
                    :xaxis="{ title: {text: $t('picker.time.title') + '(min)'}, type: 'numeric', min: 0, max: dayjs(event.data.end_at).diff(event.data.start_at, 'minute') + 1 }"
                    :yaxis="[{title: { text: $t('hr_variables.max_velocity_km_h') }, min: 0, max: 35, tickAmount: 5}]"
                    :colors="[ '#FF8888' ]"
                    :annotations="effortsAnnotations"
                    :animations="{enabled: false}"
                  />
                  <line-chart
                    v-if="tab === 'acc'"
                    class="elevation-0"
                    type="bar"
                    :raw-data="filteredAcc"
                    :chart-value-keys="[ { key: 'max', type: 'bar', label: $t('chart_legends.acceleration') }]"
                    x-value="start_time"
                    :xaxis="{ title: {text: $t('picker.time.title') + '(min)'}, type: 'numeric', min: 0, max: dayjs(event.data.end_at).diff(event.data.start_at, 'minute') + 1}"
                    :yaxis="[{title: { text: $t('mechanism.acceleration') + '(m/s²)' }, min: -4.5, max: 4.5, tickAmount: 5}]"
                    :colors="[ '#00CC00' ]"
                    :annotations="effortsAnnotations"
                    :animations="{enabled: false}"
                  />
                  <line-chart
                    v-if="tab === 'hr'"
                    class="elevation-0"
                    type="bar"
                    :raw-data="filteredHREfforts"
                    :chart-value-keys="[ { key: 'max', type: 'bar', label: $t('performance.raw.variables.heart_rate') }]"
                    x-value="start_time"
                    :xaxis="{ title: {text: 'Time (min)'}, type: 'numeric', min: 0, max: dayjs(event.data.end_at).diff(event.data.start_at, 'minute') + 1}"
                    :yaxis="[{title: { text: $t('hr_variables.max_heart_rate') }, min: 0, max: 210}]"
                    :colors="[ '#E91E63' ]"
                    :annotations="effortsAnnotations"
                    :animations="{enabled: false}"
                  />
                </v-col>
                <v-col v-else>
                  <v-data-table
                    v-if="tab === 'sprints'"
                    height="350"
                    disable-pagination
                    dense
                    :headers="sprintHeaders"
                    :items="filteredSprints"
                    hide-default-footer
                  />
                  <v-data-table
                    v-else-if="tab === 'acc'"
                    height="350"
                    disable-pagination
                    dense
                    :headers="accHeaders"
                    :items="filteredAcc"
                    hide-default-footer
                  />
                  <v-data-table
                    v-else-if="tab === 'hr'"
                    height="350"
                    disable-pagination
                    dense
                    :headers="hrHeaders"
                    :items="filteredHREfforts"
                    hide-default-footer
                  />
                </v-col>
              </v-row>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <event-player-dialog
      v-if="event && player"
      :visible.sync="eventPlayerDialog"
      :player-id="player.id"
      :event="event.data"
      @success="$emit('update')"
    />
  </div>
</template>

<script>

import HumanBodyFemaleFrontMuscles from '@/components/human-body/HumanBodyFemaleFrontMuscles.vue'
import HumanBodyBackMuscles from '@/components/human-body/HumanBodyBackMuscles.vue'
import HumanBodyFemaleBackMuscles from '@/components/human-body/HumanBodyFemaleBackMuscles.vue'
import HumanBodyFrontMuscles from '@/components/human-body/HumanBodyFrontMuscles.vue'
import { VariableApiService } from '@/data/services/VariableApiService'
import LineChart from '@/components/charts/LineChart.vue'
import dayjs from 'dayjs'
import { EventApiService } from '@/data/services'
import i18n from '@/plugins/i18n'
import EventPlayerDialog from '@/components/dialogs/EventPlayerDialog.vue'
import { getColorForValue, toCamelCase } from '@/utils/helpers'
import StatCard from '@/components/StatCard.vue'
import RadialBarChart from '@/components/charts/RadialBarChart.vue'
import { get } from 'lodash'
import { maxValuesPerformanceMatch, maxValuesPerformanceTraining } from '@/data/models/TableHeader'

export default {
  name: 'PerformanceIndividual',
  components: { RadialBarChart, StatCard, EventPlayerDialog, LineChart, HumanBodyFrontMuscles, HumanBodyFemaleBackMuscles, HumanBodyBackMuscles, HumanBodyFemaleFrontMuscles },
  props: {
    player: {
      type: Object,
      required: true
    },
    team: {
      type: Object,
      required: true
    },
    event: {
      type: Object,
      required: true
    },
    singlePlayerEvent: {
      type: Boolean,
      default: true
    },
    exercise: {
      type: Object,
      default: () => ({})
    }
  },
  data: () => ({
    variables: {
      data: [],
      loading: false
    },
    period: {
      data: null,
      loading: false
    },
    rawData: {
      data: [],
      loadingRaw: false,
      loadingEfforts: false
    },
    exerciseStats: {
      data: [],
      loading: false
    },
    selectedExercise: null,
    eventPlayerDialog: false,
    selectedPeriodId: null,
    selectedBands: [3, 4, 5, 6, 7],
    tab: 'sprints',
    view: 'visual',
    showPeriodAnnotations: false,
    showExerciseAnnotations: false,
    sprintBandsColors: ['#FFBBBB', '#FFAAAA', '#FF9999', '#FF8888', '#FF7777', '#FF6666', '#FF5555'],
    accelerationBands: ['a < -4m/s²', '-4m/s² < a < -3m/s²', '-3m/s² < a < -2m/s²', '-2m/s² < a < 0', '0 < a < 2m/s²', '2m/s² < a < 3m/s²', '3m/s² < a < 4m/s²', 'a > 4m/s²'],
    sprintBands: ['0km/h < v < 7.2km/h', '7.2km/h < v < 14.4km/h', '14.4km/h < v < 21.6km/h', '21.6km/h < v < 25.2km/h', '25.2km/h < v < 28.8km/h', '28.8km/h < v < 32.4km/h', 'v > 32.4km/h'],
    hrBands: ['20 - 45', '45 - 55', '55 - 65', '65 - 75', '75 - 85', '85 - 95', '95 - 105', '105 - 200'],
    colorMaxValuesTraining: maxValuesPerformanceTraining,
    colorMaxValuesMatch: maxValuesPerformanceMatch
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    performanceMaxValues() {
      return (this.event.data && this.event.data.event_type === 'training') ? this.colorMaxValuesTraining : this.colorMaxValuesMatch
    },
    eventParentPlayerStats() {
      return this.event.data.players.find(player => player.id === this.player.id)?.player_stats || {}
    },
    playerStats() {
      if (this.selectedExercise && this.exerciseStats.data && this.exerciseStats.data.players) {
        return this.exerciseStats.data?.players[0].player_stats || {}
      } else {
        return this.player.player_stats
      }
    },
    hasExerciseTiming() {
      return this.event.data?.training_exercises?.some(exercise => {
        if (exercise.exercise_start_at) {
          return true
        }
        if (exercise.type === 'group' && exercise.exercises?.some(subExercise => subExercise.exercise_start_at)) {
          return true
        }
        return false
      })
    },
    hasPlayerExerciseTiming() {
      return this.player.training_exercises?.some(exercise => {
        if (exercise.exercise_start_at) {
          return true
        }
        if (exercise.type === 'group' && exercise.exercises?.some(subExercise => subExercise.exercise_start_at)) {
          return true
        }
        return false
      })
    },
    playerTrainingExercises() {
      const exercises = this.player.training_exercises || []

      const result = []

      exercises.forEach(exercise => {
        if (exercise.type === 'group' && Array.isArray(exercise.exercises)) {
          result.push(...exercise.exercises)
        } else {
          result.push(exercise)
        }
      })

      return result.filter(exercise => {
        if (exercise.type === 'pause') return false
        if (exercise.type === 'superset') {
          return exercise.exercises[0].exercise_start_at
        } else {
          return exercise.exercise_start_at
        }
      })
    },
    itlVariables() {
      return [
        {
          value: 'TTM',
          text: this.$t('variables.TTM'),
          colorable: false,
          max: null,
          reverse: null
        },
        {
          value: 'TG',
          text: this.$t('variables.TG'),
          colorable: true,
          max: 10,
          reverse: true
        },
        {
          value: 'TL',
          text: this.$t('variables.TL'),
          colorable: false,
          max: null,
          reverse: null
        },
        {
          value: 'ENJY',
          text: this.$t('variables.ENJY'),
          colorable: true,
          max: 5,
          reverse: false
        }
      ]
    },
    variablesRR() {
      if (!this.variables.data.length) return []

      return this.variables.data.filter(variable => variable.category === 'RR' && this.playerStats[variable.value])
    },
    variablesDistancePerMin() {
      return [
        { key: 'UXLSPmin', label: this.$t('variables.UXLSPmin'), value: this.playerStats.UXLSPmin || 0 },
        { key: 'UXHSRmin', label: this.$t('variables.UXHSRmin'), value: this.playerStats.UXHSRmin || 0 },
        { key: 'UXPCSMYPM', label: this.$t('variables.UXPCSMYPM'), value: this.playerStats.UXPCSMYPM || 0 }
      ]
    },
    variablesMetabolic() {
      return ['UXPCSTPL', 'CALETLDMP', 'UXMPTOTAL', 'UXAERAVGMP', 'UXANAENRG', 'UXANAINDX', 'UXENGEXP', 'UXHMLD', 'UXEQDIST', 'UXEQDISTINDX', 'UXMPEAVGDR', 'UXMPERECAVGT', 'UXNUMMPE', 'UXMPETDR']
    },
    filteredMetabolic() {
      return this.variablesMetabolic.filter(variable => this.playerStats[variable])
    },
    speedZones() {
      return [
        { key: 'CALETLDLSP', label: this.$t('variables.zone5'), value: this.playerStats.CALETLDLSP || 0 },
        { key: 'CALETLDLHSR', label: this.$t('variables.zone4'), value: this.playerStats.CALETLDLHSR || 0 },
        { key: 'CACTCSVB3TD', label: this.$t('variables.zone3'), value: this.playerStats.CACTCSVB3TD || 0 },
        { key: 'CACTCSVB2TD', label: this.$t('variables.zone2'), value: this.playerStats.CACTCSVB2TD || 0 },
        { key: 'CACTCSVB1TD', label: this.$t('variables.zone1'), value: this.playerStats.CACTCSVB1TD || 0 }
      ]
    },
    accelerationZones() {
      return [
        { key: 'CAPGCSACB13EG2', label: this.$t('variables.zone4'), Accelerations: this.playerStats.CAPGCSACB13EG2 || 0, Decelerations: -(this.playerStats.CAPGCSDCB13EG2 || 0) },
        { key: 'UXACB68', label: this.$t('variables.zone3'), Accelerations: this.playerStats.UXACB68 || 0, Decelerations: -(this.playerStats.UXDCB13 || 0) },
        { key: 'UXACB58', label: this.$t('variables.zone2'), Accelerations: this.playerStats.UXACB58 || 0, Decelerations: -(this.playerStats.UXDCB14 || 0) }
      ]
    },
    velocityZones() {
      return [
        { key: 'UXVELB67', label: this.$t('variables.zone6'), value: this.playerStats.UXVELB67 || 0 },
        { key: 'UXVELB57', label: this.$t('variables.zone5'), value: this.playerStats.UXVELB57 || 0 },
        { key: 'UXVELB47', label: this.$t('variables.zone4'), value: this.playerStats.UXVELB47 || 0 },
        { key: 'UXVELB37', label: this.$t('variables.zone3'), value: this.playerStats.UXVELB37 || 0 },
        { key: 'UXVELB27', label: this.$t('variables.zone2'), value: this.playerStats.UXVELB27 || 0 },
        { key: 'UXVELB17', label: this.$t('variables.zone1'), value: this.playerStats.UXVELB17 || 0 }
      ]
    },
    hrZones() {
      return [
        { key: 'ZONE5DR', label: this.$t('variables.zone5'), value: this.playerStats.ZONE5DR || 0 },
        { key: 'ZONE4DR', label: this.$t('variables.zone4'), value: this.playerStats.ZONE4DR || 0 },
        { key: 'ZONE3DR', label: this.$t('variables.zone3'), value: this.playerStats.ZONE3DR || 0 },
        { key: 'ZONE2DR', label: this.$t('variables.zone2'), value: this.playerStats.ZONE2DR || 0 },
        { key: 'ZONE1DR', label: this.$t('variables.zone1'), value: this.playerStats.ZONE1DR || 0 }
      ]
    },
    heartRateLogSeries() {
      const data = this.rawData.data.HEART_RATE ?? []

      const timestamps = this.rawData.data.TIMESTAMP ?? []

      const series = []

      const dateStart = dayjs(this.event.data.start_at)

      for (const [index, d] of data.entries()) {
        series.push(timestamps[index] ? [timestamps[index] * 1000, d] : {
          x: dateStart.add(index, 'second').format(),
          y: d
        })
      }
      return series
    },
    speedSeries() {
      const data = this.rawData.data.SPEED ?? []

      const timestamps = this.rawData.data.TIMESTAMP ?? []

      const series = []

      const dateStart = dayjs(this.event.data.start_at)

      for (const [index, d] of data.entries()) {
        series.push(timestamps[index] ? [timestamps[index] * 1000, d] : {
          x: dateStart.add(index, 'second').format(),
          y: d
        })
      }

      return series
    },
    periodsAnnotations() {
      return this.event.data.periods.map(period => {
        return {
          x: dayjs(period.start_at).valueOf(),
          x2: dayjs(period.end_at).valueOf(),
          borderColor: '#000000',
          opacity: 0.4,
          label: {
            text: period.title,
            offsetX: 17,
            color: '#000000',
            style: {
              background: 'rgba(225,225,225,0.7)'
            }
          }
        }
      })
    },
    heartRateLogAnnotations() {
      const hrProfile = this.player.hr_profile

      const method = hrProfile.method

      let addKey = ''

      if (method === 'HRANP') addKey = 'ANPM'

      const zones = [
        { key: 'HRREG', color: '#bfbfbf', label: this.$t('hr_variables.regeneration_zone') },
        { key: 'HRRE', color: '#008FFB', label: this.$t('hr_variables.extensive_aerobic_zone') },
        { key: 'HRAI', color: '#00E396', label: this.$t('hr_variables.intensive_aerobic_zone') },
        { key: 'HRANP', color: '#FEB019', label: this.$t('hr_variables.anaerobic_threshold_zone') },
        { key: 'HRVO2MAX', color: '#FF4560', label: this.$t('hr_variables.vo2_max_zone') }
      ]

      return {
        yaxis: (!hrProfile || !this.rawData.data.HEART_RATE.length) ? [] : zones.map(z => {
          return {
            y: hrProfile.calculations[z.key + addKey].lower,
            y2: hrProfile.calculations[z.key + addKey].upper,
            opacity: 0.5,
            fillColor: z.color,
            borderColor: z.color,
            label: {
              text: z.label,
              offsetY: 17,
              offsetX: -5,
              style: {
                background: 'transparent'
              }
            }
          }
        }),
        xaxis: this.xAxisAnnotations
      }
    },
    filteredSprints() {
      if (!this.rawData.data?.sprints) return []

      if (this.selectedBands.length === 0) return this.rawData.data.sprints

      return this.rawData.data.sprints.filter(s => this.selectedBands.includes(s.band))
    },
    filteredAcc() {
      if (!this.rawData.data?.high_acc) return []

      if (this.selectedBands.length === 0) return this.rawData.data.high_acc

      return this.rawData.data.high_acc.filter(s => this.selectedBands.includes(s.band))
    },
    filteredHREfforts() {
      if (!this.rawData.data?.hr) return []

      if (this.selectedBands.length === 0) return this.rawData.data.hr

      return this.rawData.data.hr.filter(s => this.selectedBands.includes(s.band))
    },
    xAxisAnnotations() {
      if (this.showPeriodAnnotations) {
        return this.periodsAnnotations
      } else if (this.showExerciseAnnotations) {
        return this.exerciseAnnotations
      } else {
        let exerciseStart = 0
        let exerciseStop = null
        if (this.selectedExercise) {
          exerciseStart = this.selectedExercise.type === 'superset' ? this.selectedExercise?.exercises[0].exercise_start_at : this.selectedExercise?.exercise_start_at
          exerciseStop = this.selectedExercise.type === 'superset' ? this.selectedExercise?.exercises[0].exercise_stop_at : this.selectedExercise?.exercise_stop_at
        } else {
          exerciseStart = this.exercise ? this.exercise.type === 'superset' ? this.exercise?.exercises[0].exercise_start_at : this.exercise?.exercise_start_at : null
          exerciseStop = this.exercise ? this.exercise.type === 'superset' ? this.exercise?.exercises[0].exercise_stop_at : this.exercise?.exercise_stop_at : null
        }

        return [{
          x: this.exercise ? this.$dayjs(exerciseStart).utc().valueOf() : 0,
          x2: this.exercise ? this.$dayjs(exerciseStop).utc().valueOf() : null,
          fillColor: null,
          opacity: 0.15,
          label: {
            text: this.exercise.type === 'superset' ? this.$t('training_builder.superset') : this.exercise.name,
            offsetX: 17,
            color: '#000000',
            style: {
              background: 'rgba(225,225,225,0.7)'
            }
          }
        }]
      }
    },
    exerciseAnnotations() {
      return this.event.data.training_exercises.map(exercise => {
        const exerciseStart = exercise.type === 'superset' ? exercise?.exercises[0].exercise_start_at : exercise?.exercise_start_at
        const exerciseStop = exercise.type === 'superset' ? exercise?.exercises[0].exercise_stop_at : exercise?.exercise_stop_at

        return {
          x: this.$dayjs(exerciseStart).utc().valueOf(),
          x2: this.$dayjs(exerciseStop).utc().valueOf(),
          borderColor: '#000000',
          label: {
            text: exercise.type === 'superset' ? this.$t('training_builder.superset') : exercise.name,
            offsetX: 17,
            color: '#000000',
            style: {
              background: 'rgba(225,225,225,0.7)'
            }
          }
        }
      })
    },
    effortsAnnotations() {
      let annotations = []

      if (this.showPeriodAnnotations) {
        annotations = this.event.data.periods.map(period => {
          return {
            x: dayjs(period.start_at).diff(this.event.data.start_at, 'minute', true),
            x2: dayjs(period.end_at).diff(this.event.data.start_at, 'minute', true),
            borderColor: '#000000',
            label: {
              text: period.title,
              offsetX: 17,
              color: '#000000',
              style: {
                background: 'rgba(225,225,225,0.7)'
              }
            }
          }
        })
      } else if (this.showExerciseAnnotations) {
        const eventStart = dayjs(this.event.data.start_at)

        annotations = this.event.data.training_exercises.map(exercise => {
          return {
            x: dayjs(`${eventStart.format('YYYY-MM-DD')}T${exercise.exercise_start_at}`).diff(eventStart, 'minute', true),
            x2: dayjs(`${eventStart.format('YYYY-MM-DD')}T${exercise.exercise_stop_at}`).diff(eventStart, 'minute', true),
            borderColor: '#000000',
            label: {
              text: exercise.name,
              offsetX: 17,
              color: '#000000',
              style: {
                background: 'rgba(225,225,225,0.7)'
              }
            }
          }
        })
      } else {
        const eventStart = dayjs(this.event.data.start_at)

        annotations = [{
          x: this.exercise ? dayjs(`${eventStart.format('YYYY-MM-DD')}T${this.exercise?.exercise_start_at}`).diff(eventStart, 'minute', true) : 0,
          x2: this.exercise ? dayjs(`${eventStart.format('YYYY-MM-DD')}T${this.exercise?.exercise_stop_at}`).diff(eventStart, 'minute', true) : null,
          fillColor: null,
          opacity: 0.15,
          label: {
            text: this.exercise.name,
            offsetX: 17,
            color: '#000000',
            style: {
              background: 'rgba(225,225,225,0.7)'
            }
          }
        }]
      }

      return {
        xaxis: annotations
      }
    },
    possibleBands() {
      if (this.tab === 'sprints') {
        return 7
      } else if (this.tab === 'acc') {
        return 8
      } else if (this.tab === 'hr') {
        return 8
      } else {
        return null
      }
    },
    hasEfforts() {
      if (this.tab === 'sprints') {
        return this.rawData.data?.sprints?.length > 0
      } else if (this.tab === 'acc') {
        return this.rawData.data?.high_acc?.length > 0
      } else if (this.tab === 'hr') {
        return this.rawData.data?.hr?.length > 0
      } else {
        return false
      }
    }
  },
  watch: {
    tab: function (value) {
      this.selectedBands = this.tab === 'sprints' ? [3, 4, 5, 6, 7] : []

      this.fetchPlayerRawData(value === 'sprints' ? 'vel_eff' : (value === 'acc' ? 'acc_eff' : (value === 'hr' ? 'hr_eff' : null)))
    },
    event: function (val) {
      if (val.id) {
        this.fetchPlayerRawData('raw')
        this.fetchPlayerRawData('vel_eff')
      }
    },
    selectedExercise: function (val) {
      if (val) {
        this.fetchStatsByExercise()
      }
    }
  },
  created() {
    this.fetchVariables()
    this.fetchPlayerRawData('raw')
    this.fetchPlayerRawData('vel_eff')

    this.sprintHeaders = [
      { value: 'start_time_min', text: i18n.t('table_headers.performance.player_details.efforts.start_time') },
      { value: 'end_time_min', text: i18n.t('table_headers.performance.player_details.efforts.end_time') },
      { value: 'band', text: i18n.t('table_headers.performance.player_details.efforts.band') },
      { value: 'distance', text: i18n.t('table_headers.performance.player_details.efforts.distance') },
      { value: 'duration_min', text: i18n.t('training_builder.text_input.duration.title') },
      { value: 'max', text: i18n.t('table_headers.performance.player_details.efforts.vmax') },
      { value: 'average', text: i18n.t('table_headers.performance.player_details.efforts.vavg') },
      { value: 'one_seconds_before', text: i18n.t('table_headers.performance.player_details.efforts.one_second_before') },
      { value: 'two_seconds_before', text: i18n.t('table_headers.performance.player_details.efforts.two_seconds_before') },
      { value: 'three_seconds_before', text: i18n.t('table_headers.performance.player_details.efforts.three_seconds_before') },
      { value: 'acc_max', text: i18n.t('table_headers.performance.player_details.efforts.acc_max') },
      { value: 'hr_max', text: i18n.t('table_headers.performance.player_details.efforts.hr_max') }
    ]

    this.accHeaders = [
      { value: 'start_time_min', text: i18n.t('table_headers.performance.player_details.efforts.start_time') },
      { value: 'end_time_min', text: i18n.t('table_headers.performance.player_details.efforts.end_time') },
      { value: 'band', text: i18n.t('table_headers.performance.player_details.efforts.band') },
      { value: 'distance', text: i18n.t('table_headers.performance.player_details.efforts.distance') },
      { value: 'duration_min', text: i18n.t('training_builder.text_input.duration.title') },
      { value: 'max', text: i18n.t('table_headers.performance.player_details.efforts.peek') },
      { value: 'average', text: i18n.t('table_headers.performance.player_details.efforts.avg_acc') },
      { value: 'one_seconds_before', text: i18n.t('table_headers.performance.player_details.efforts.one_second_before') },
      { value: 'two_seconds_before', text: i18n.t('table_headers.performance.player_details.efforts.two_seconds_before') },
      { value: 'three_seconds_before', text: i18n.t('table_headers.performance.player_details.efforts.three_seconds_before') },
      { value: 'speed_max', text: i18n.t('table_headers.performance.player_details.efforts.speed_max') },
      { value: 'hr_max', text: i18n.t('table_headers.performance.player_details.efforts.hr_max') }
    ]

    this.hrHeaders = [
      { value: 'start_time', text: i18n.t('table_headers.performance.player_details.efforts.start_time') },
      { value: 'end_time', text: i18n.t('table_headers.performance.player_details.efforts.end_time') },
      { value: 'band', text: i18n.t('table_headers.performance.player_details.efforts.band') },
      { value: 'distance', text: i18n.t('table_headers.performance.player_details.efforts.distance') },
      { value: 'max', text: i18n.t('table_headers.performance.player_details.efforts.max_hr') },
      { value: 'average', text: i18n.t('table_headers.performance.player_details.efforts.avg_hr') },
      { value: 'speed_max', text: i18n.t('table_headers.performance.player_details.efforts.speed_max') },
      { value: 'acc_max', text: i18n.t('table_headers.performance.player_details.efforts.acc_max') }
    ]
  },
  methods: {
    dayjs,
    get,
    getColorForValue,
    toCamelCase,
    async fetchVariables() {
      this.variables.loading = true

      try {
        const response = await VariableApiService.getAll()

        this.variables.data = response.data.map(v => {
          return {
            ...v,
            text: v.suffix ? this.$t(`variables.${v.value}`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}`),
            long_text: v.suffix ? this.$t(`variables.${v.value}.long`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}.long`)
          }
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    },
    async fetchPeriod(periodId) {
      this.period.loading = true

      try {
        const response = await EventApiService.getEventDetails(periodId)

        this.period.data = response.data
      } catch (e) {
        this.period.data = []
        throw e
      } finally {
        this.period.loading = false
      }
    },
    async fetchPlayerRawData(dataType) {
      if (dataType === 'raw') {
        this.rawData.loadingRaw = true
      } else {
        this.rawData.loadingEfforts = true
      }

      try {
        const params = {
          player_id: this.player.id,
          data_type: dataType
        }

        const data = await EventApiService.getPlayerPerformanceData(this.event.data.id, params)

        if (data && data.sprints) {
          data.sprints = data.sprints.map(s => {
            s.start_time_min = Math.floor(s.start_time) + 'm ' + Math.floor((s.start_time - Math.floor(s.start_time)) * 60) + 's'
            s.end_time_min = Math.floor(s.end_time) + 'm ' + Math.floor((s.end_time - Math.floor(s.end_time)) * 60) + 's'
            s.duration_min = Math.floor(s.duration) + 'm ' + Math.floor((s.duration - Math.floor(s.duration)) * 60) + 's'

            return s
          })
        }

        if (data && data.high_acc) {
          data.high_acc = data.high_acc.map(s => {
            s.start_time_min = Math.floor(s.start_time) + 'm ' + Math.floor((s.start_time - Math.floor(s.start_time)) * 60) + 's'
            s.end_time_min = Math.floor(s.end_time) + 'm ' + Math.floor((s.end_time - Math.floor(s.end_time)) * 60) + 's'
            s.duration_min = Math.floor(s.duration) + 'm ' + Math.floor((s.duration - Math.floor(s.duration)) * 60) + 's'

            if (s.band <= 4) s.max = -Math.abs(s.max)

            return s
          })
        }

        this.rawData.data = { ...this.rawData.data, ...data }
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: 'There were some errors loading player stats',
          color: 'error'
        })

        throw e
      } finally {
        this.rawData.loadingRaw = false
        this.rawData.loadingEfforts = false
      }
    },
    async fetchStatsByExercise() {
      if (this.exerciseStats.loading) return

      try {
        this.exerciseStats.loading = true

        const params = {
          player_ids: [this.player.id],
          event_ids: [this.event.data.id],
          training_protocol_exercise_ids: this.selectedExercise.type === 'superset' ? [this.selectedExercise.exercises[0].internal_id] : [this.selectedExercise.internal_id]
        }

        this.exerciseStats.data = await EventApiService.getAdvancedPerformanceStats(params)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_advanced_statistics'),
          color: 'error'
        })

        throw e
      } finally {
        this.exerciseStats.loading = false
      }
    },
    getColorForTooltip(val) {
      if (this.tab === 'sprints') {
        return this.sprintBandsColors[val - 1]
      } else if (this.tab === 'acc') {
        return val <= 4 ? '#00CC00' : '#00FF00'
      } else if (this.tab === 'hr') {
        return '#E91E63'
      }
    },
    getTooltipText(val) {
      if (this.tab === 'sprints') {
        return this.sprintBands[val - 1]
      } else if (this.tab === 'acc') {
        return this.accelerationBands[val - 1]
      } else if (this.tab === 'hr') {
        return this.hrBands[val - 1]
      }
    },
    getVariableName(value) {
      return this.variables.data.find(variable => variable.value === value)?.text
    },
    getVariableSuffix(value) {
      return this.variables.data.find(variable => variable.value === value)?.suffix
    },
    getColorFromValue(value, max, reverse = false) {
      return getColorForValue(value, max, reverse)
    }
  }
}
</script>
